import type { AgentDto, AgentDtoHealthState } from '@/api/control/missionImpactAPI.schemas'

/**
 * The stage of an agent health check, as shown to the user.
 * While a check runs it moves from `checking` to `potentialIssue` (60s) to `likelyIssue` (120s);
 * the server records the final outcome (by 180s, or as soon as the agent replies).
 */
export type HealthCheckStage =
  | 'idle'
  | 'checking'
  | 'potentialIssue'
  | 'likelyIssue'
  | 'healthy'
  | 'busy'
  | 'unresponsive'
  | 'inconclusive'

/** After this long without a reply, the check shows a potential health issue. */
export const POTENTIAL_ISSUE_AFTER_MS = 60_000

/** After this long without a reply, the check shows a likely health issue. */
export const LIKELY_ISSUE_AFTER_MS = 120_000

/** A check still unfinished after this long is treated as abandoned (matches the server). */
export const CHECK_ABANDONED_AFTER_MS = 10 * 60_000

/** How long a completed check's result stays visible next to the agent. */
export const RESULT_VISIBLE_FOR_MS = 15 * 60_000

type HealthCheckFields = Pick<
  AgentDto,
  'lastHealthCheckStartedUtc' | 'lastHealthCheckCompletedUtc' | 'lastHealthCheckOutcome'
>

/**
 * Returns the stage of the agent's most recent health check at the given time.
 */
export function getHealthCheckStage(agent: HealthCheckFields, nowMs: number): HealthCheckStage {
  if (!agent.lastHealthCheckStartedUtc) {
    return 'idle'
  }

  const startedMs = new Date(agent.lastHealthCheckStartedUtc).getTime()

  if (!agent.lastHealthCheckCompletedUtc) {
    const elapsedMs = nowMs - startedMs
    if (elapsedMs >= CHECK_ABANDONED_AFTER_MS) return 'idle'
    if (elapsedMs >= LIKELY_ISSUE_AFTER_MS) return 'likelyIssue'
    if (elapsedMs >= POTENTIAL_ISSUE_AFTER_MS) return 'potentialIssue'
    return 'checking'
  }

  if (nowMs - new Date(agent.lastHealthCheckCompletedUtc).getTime() > RESULT_VISIBLE_FOR_MS) {
    return 'idle'
  }

  switch (agent.lastHealthCheckOutcome) {
    case 'Healthy':
      return 'healthy'
    case 'Busy':
      return 'busy'
    case 'Unresponsive':
      return 'unresponsive'
    case 'Inconclusive':
      return 'inconclusive'
    default:
      return 'idle'
  }
}

/**
 * Whether a health check is still waiting for the agent.
 */
export function isHealthCheckRunning(stage: HealthCheckStage): boolean {
  return stage === 'checking' || stage === 'potentialIssue' || stage === 'likelyIssue'
}

/**
 * Returns the badge variant for an agent health state.
 */
export function getHealthBadgeVariant(state: AgentDtoHealthState): 'outline' | 'secondary' | 'destructive' {
  switch (state) {
    case 'Offline':
      return 'destructive'
    case 'Suspect':
      return 'secondary'
    default:
      return 'outline'
  }
}

/**
 * Returns the badge variant for a health check stage: problems are destructive, waiting is secondary.
 */
export function getHealthCheckStageVariant(stage: HealthCheckStage): 'outline' | 'secondary' | 'destructive' {
  switch (stage) {
    case 'likelyIssue':
    case 'unresponsive':
      return 'destructive'
    case 'checking':
    case 'potentialIssue':
    case 'busy':
    case 'inconclusive':
      return 'secondary'
    default:
      return 'outline'
  }
}

/** How often agent data is refreshed while a health check is waiting for the agent. */
export const RUNNING_CHECK_REFRESH_MS = 5_000

/**
 * Returns the refresh interval for agent data: frequent while any agent's health check is running
 * (so its result appears promptly), otherwise false (no polling).
 */
export function getHealthCheckRefreshInterval(
  agents: readonly HealthCheckFields[] | undefined,
  nowMs: number,
): number | false {
  return agents?.some((agent) => isHealthCheckRunning(getHealthCheckStage(agent, nowMs)))
    ? RUNNING_CHECK_REFRESH_MS
    : false
}
